import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { useLocation, Link } from "wouter";
import { DEFAULT_THUMBNAIL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { LANGUAGES, type Class, type Session, type Recording, type RecordingTag, type ScheduleApproval } from "@shared/schema";
import { format, formatDistanceToNow, isPast, isFuture, addDays, addWeeks, addMonths, setDay, getDay, startOfDay } from "date-fns";
import {
  Avatar, AvatarFallback,
} from "@/components/ui/avatar";
import { DatePicker, TimePicker } from "@/components/ui/date-time-picker";
import {
  Plus, BookOpen, Calendar, Settings, Play, StopCircle,
  Edit, Trash2, Video, Clock, LayoutDashboard, MessageSquare, Sparkles,
  Zap, Users, BarChart3, ArrowRight, Eye, ExternalLink, ChevronRight,
  Tag, Globe, Check, CircleDot, Loader2, Copy, Search, X, UserPlus, Filter, Scissors,
  ClipboardCheck, RefreshCw, AlertTriangle, ChevronUp, ChevronDown,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";

function getFlagEmoji(code: string) {
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)));
}

interface ProviderStats {
  totalClasses: number;
  totalSessions: number;
  liveSessions: number;
  scheduledSessions: number;
  endedSessions: number;
}

export default function ProviderDashboard() {
  const { user, isLoading } = useAuth();
  const [, navigate] = useLocation();

  const needsRedirect = !isLoading && (!user || (user.role !== "PROVIDER" && user.role !== "ADMIN" && user.role !== "SUPER_ADMIN"));

  useEffect(() => {
    if (needsRedirect) {
      navigate("/login");
    }
  }, [needsRedirect, navigate]);

  if (isLoading || needsRedirect) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] bg-background" data-testid="status-provider-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      <div className="glass-page-bg" />
      <DashboardHeader userName={user.name} />
      <ShareableRoomLink />
      <StatsOverview />
      <LiveNowBanner />
      <div className="mt-6">
        <ClassesPanel />
      </div>
    </div>
  );
}

function DashboardHeader({ userName }: { userName: string }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl glow-text" data-testid="text-provider-title">
          <LayoutDashboard className="mr-2 inline h-6 w-6 text-primary" />
          Studio Dashboard
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Welcome back, {userName}. Manage your classes and go live.
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <GoLiveNowDialog />
        <CreateClassDialog />
      </div>
    </div>
  );
}

function ShareableRoomLink() {
  const { data, isLoading } = useQuery<{ roomSlug: string; shareUrl: string | null }>({
    queryKey: ["/api/provider/me/room-slug"],
  });
  const [copied, setCopied] = useState(false);

  if (isLoading || !data?.shareUrl) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.shareUrl!);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="mb-4 p-3 sm:p-4 glass-share-link" data-testid="card-shareable-link">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <ExternalLink className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm font-medium truncate">Your Room Link:</span>
          <code className="hidden sm:inline rounded bg-muted/30 px-2 py-0.5 text-xs text-muted-foreground truncate max-w-[300px]" data-testid="text-share-url">
            {data.shareUrl}
          </code>
        </div>
        <div className="flex items-center gap-2">
          <code className="sm:hidden flex-1 rounded bg-muted/30 px-2 py-1 text-xs text-muted-foreground truncate" data-testid="text-share-url-mobile">
            {data.shareUrl}
          </code>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCopy}
            className="shrink-0 gap-1.5"
            data-testid="button-copy-room-link"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied!" : "Copy"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatsOverview() {
  const { data: stats, isLoading } = useQuery<ProviderStats>({
    queryKey: ["/api/provider/stats"],
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="glass-stat p-4"><Skeleton className="h-12 w-full" /></div>
        ))}
      </div>
    );
  }

  const statItems = [
    { label: "Classes", value: stats?.totalClasses || 0, icon: BookOpen, color: "text-primary" },
    { label: "Live Now", value: stats?.liveSessions || 0, icon: null, color: "text-red-400" },
    { label: "Upcoming", value: stats?.scheduledSessions || 0, icon: Calendar, color: "text-amber-400" },
    { label: "Completed", value: stats?.endedSessions || 0, icon: BarChart3, color: "text-emerald-400" },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="stats-overview">
      {statItems.map((item, idx) => (
        <div key={item.label} className={`glass-stat depth-shadow-sm p-4 animate-fade-in-up stagger-${idx + 1} ${item.label === "Live Now" && (item.value as number) > 0 ? "glass-stat-live" : ""}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs text-muted-foreground section-label">{item.label}</p>
              <p className="mt-1 text-2xl font-bold glow-text-sm" data-testid={`stat-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {item.value}
              </p>
            </div>
            <div className="relative">
              {item.label === "Live Now" ? (
                <LiveBroadcastIcon size={22} />
              ) : item.icon ? (
                <item.icon className={`h-5 w-5 ${item.color}`} />
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LiveNowBanner() {
  const { data: classes } = useQuery<(Class & { sessions: Session[] })[]>({
    queryKey: ["/api/provider/classes"],
  });

  const liveSessions: { session: Session; cls: Class }[] = [];
  classes?.forEach((cls) => {
    cls.sessions?.forEach((s) => {
      if (s.status === "LIVE") liveSessions.push({ session: s, cls });
    });
  });

  if (liveSessions.length === 0) return null;

  return (
    <div className="mt-4" data-testid="live-now-banner">
      {liveSessions.map(({ session, cls }) => (
        <div key={session.id} className="glass-panel-sm border-red-500/30 !bg-red-500/5 p-4 ml-[0px] mr-[0px] pt-[16px] pb-[16px] pl-[16px] pr-[16px] mt-[10px] mb-[10px]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
              </span>
              <div>
                <p className="font-semibold">{cls.title}</p>
                <p className="text-xs text-muted-foreground">
                  Live since {format(new Date(session.startsAt), "h:mm a")}
                </p>
              </div>
            </div>
            <Link href={`/live/${session.id}`}>
              <Button data-testid={`button-enter-live-${session.id}`}>
                <Video className="mr-1.5 h-4 w-4" />
                Enter Room
              </Button>
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

function ClassesPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const { data: classes, isLoading } = useQuery<(Class & { sessions: Session[]; providerName?: string; shareUrl?: string | null })[]>({
    queryKey: ["/api/provider/classes"],
  });

  const { data: providersList } = useQuery<{ id: string; name: string; email: string; role: string }[]>({
    queryKey: ["/api/provider/providers-list"],
    enabled: isAdmin,
  });

  const [activeTab, setActiveTab] = useState("all");
  const [providerFilter, setProviderFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredClasses = useMemo(() => {
    if (!classes) return [];
    let result = classes;
    if (providerFilter !== "ALL") {
      result = result.filter(c => c.providerId === providerFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(q) ||
        (c.providerName && c.providerName.toLowerCase().includes(q)) ||
        c.language.toLowerCase().includes(q)
      );
    }
    return result;
  }, [classes, providerFilter, searchQuery]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="glass-panel p-6"><Skeleton className="h-24 w-full" /></div>
        ))}
      </div>
    );
  }

  if (!classes || classes.length === 0) {
    return (
      <div className="glass-panel depth-shadow-md flex flex-col items-center justify-center p-12 text-center">
        <div className="empty-state-icon mb-4">
          <BookOpen className="h-12 w-12 text-primary/25" />
        </div>
        <p className="font-semibold glow-text-sm">No classes yet</p>
        <p className="mt-2 text-sm text-muted-foreground/50 max-w-xs leading-relaxed">Create your first class to get started</p>
        <div className="divider-glow w-24 my-5" />
      </div>
    );
  }

  const allSessions: { session: Session; cls: Class }[] = [];
  filteredClasses.forEach((cls) => {
    cls.sessions?.forEach((s) => allSessions.push({ session: s, cls }));
  });

  const liveSessions = allSessions.filter((s) => s.session.status === "LIVE");
  const upcomingSessions = allSessions
    .filter((s) => s.session.status === "SCHEDULED")
    .sort((a, b) => new Date(a.session.startsAt).getTime() - new Date(b.session.startsAt).getTime());
  const pastSessions = allSessions
    .filter((s) => s.session.status === "ENDED")
    .sort((a, b) => new Date(b.session.startsAt).getTime() - new Date(a.session.startsAt).getTime());

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-3 mb-4 glass-panel-sm p-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search classes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 glass-input"
              data-testid="input-provider-class-search"
            />
          </div>
          <Select value={providerFilter} onValueChange={setProviderFilter}>
            <SelectTrigger className="w-52 glass-input" data-testid="select-provider-filter">
              <Filter className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
              <SelectValue placeholder="Filter by provider" />
            </SelectTrigger>
            <SelectContent className="glass-select-content">
              <SelectItem value="ALL">All Providers</SelectItem>
              {providersList?.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(providerFilter !== "ALL" || searchQuery) && (
            <div className="text-sm text-muted-foreground">
              {filteredClasses.length} of {classes.length} classes
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="w-full overflow-x-auto sm:w-auto">
          <TabsList data-testid="tabs-dashboard" className="inline-flex w-auto glass-tabs">
            <TabsTrigger value="all" className="glass-tab-trigger" data-testid="tab-all-classes">
              <BookOpen className="mr-1.5 h-3.5 w-3.5" />
              <span className="hidden sm:inline">Classes</span> ({filteredClasses.length})
            </TabsTrigger>
            <TabsTrigger value="sessions" className="glass-tab-trigger" data-testid="tab-sessions">
              <Calendar className="mr-1.5 h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sessions</span> ({allSessions.length})
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="glass-tab-trigger" data-testid="tab-upcoming">
              <Clock className="mr-1.5 h-3.5 w-3.5" />
              <span className="hidden sm:inline">Upcoming</span> ({upcomingSessions.length})
            </TabsTrigger>
            <TabsTrigger value="recordings" className="glass-tab-trigger" data-testid="tab-recordings">
              <Video className="mr-1.5 h-3.5 w-3.5" />
              <span className="hidden sm:inline">Recordings</span>
            </TabsTrigger>
            {!isAdmin && (
              <TabsTrigger value="approvals" className="glass-tab-trigger" data-testid="tab-approvals">
                <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />
                <span className="hidden sm:inline">Change Requests</span>
              </TabsTrigger>
            )}
          </TabsList>
        </div>
      </div>

      <TabsContent value="all" className="space-y-4 mt-0">
        {filteredClasses.length === 0 ? (
          <div className="glass-panel-sm p-8 text-center text-muted-foreground">
            {searchQuery || providerFilter !== "ALL"
              ? "No classes match your filters"
              : "No classes yet"}
          </div>
        ) : (
          filteredClasses.map((cls) => (
            <ProviderClassCard key={cls.id} cls={cls} />
          ))
        )}
      </TabsContent>

      <TabsContent value="sessions" className="space-y-3 mt-0">
        {upcomingSessions.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-amber-400" /> Upcoming
            </h3>
            {upcomingSessions.map(({ session, cls }) => (
              <SessionCard key={session.id} session={session} cls={cls} />
            ))}
          </div>
        )}
        {liveSessions.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2 mt-4 flex items-center gap-1.5">
              <LiveBroadcastIcon size={16} /> Live Now
            </h3>
            {liveSessions.map(({ session, cls }) => (
              <SessionCard key={session.id} session={session} cls={cls} />
            ))}
          </div>
        )}
        {pastSessions.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2 mt-4 flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5 text-emerald-400" /> Completed
            </h3>
            {pastSessions.slice(0, 10).map(({ session, cls }) => (
              <SessionCard key={session.id} session={session} cls={cls} />
            ))}
          </div>
        )}
        {allSessions.length === 0 && (
          <div className="glass-panel p-8 text-center">
            <Calendar className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No sessions scheduled yet</p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="upcoming" className="space-y-3 mt-0">
        {upcomingSessions.length > 0 ? (
          upcomingSessions.map(({ session, cls }) => (
            <SessionCard key={session.id} session={session} cls={cls} showCountdown />
          ))
        ) : (
          <div className="glass-panel p-8 text-center">
            <Clock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No upcoming sessions</p>
            <p className="mt-1 text-xs text-muted-foreground">Schedule a session from one of your classes</p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="recordings" className="space-y-4 mt-0">
        <ProviderRecordingsTab />
      </TabsContent>

      {!isAdmin && (
        <TabsContent value="approvals" className="space-y-4 mt-0">
          <ProviderApprovalsTab />
        </TabsContent>
      )}
    </Tabs>
  );
}

function SessionCard({ session, cls, showCountdown }: { session: Session; cls: Class; showCountdown?: boolean }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const startSession = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/sessions/${session.id}/start`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      toast({ title: "Session is live!" });
      navigate(`/live/${session.id}`);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const endSession = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/sessions/${session.id}/end`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      toast({ title: "Session ended" });
    },
  });

  const startsAt = new Date(session.startsAt);
  const isLive = session.status === "LIVE";
  const isScheduled = session.status === "SCHEDULED";
  const isEnded = session.status === "ENDED";

  return (
    <div className={`glass-panel-sm p-4 mb-2 ${isLive ? "border-red-500/30" : ""}`} data-testid={`session-card-${session.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {isLive && (
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{cls.title}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
              <Badge
                variant={isLive ? "default" : isEnded ? "secondary" : "outline"}
                className={`text-[10px] ${isLive ? "bg-red-500/80 border-red-500/40" : ""}`}
              >
                {session.status}
              </Badge>
              <span className="text-[11px] text-muted-foreground">
                {format(startsAt, "MMM d, h:mm a")}
              </span>
              {showCountdown && isScheduled && isFuture(startsAt) && (
                <span className="text-[11px] text-amber-400">
                  {formatDistanceToNow(startsAt, { addSuffix: true })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <SessionSettingsDialog session={session} />
          {!isEnded && (
            <SessionSettingsDialog session={session} defaultTab="cohosts">
              <Button size="sm" variant="ghost" className="gap-1" data-testid={`button-cohosts-${session.id}`}>
                <UserPlus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-xs">Co-Hosts</span>
              </Button>
            </SessionSettingsDialog>
          )}
          {isScheduled && (
            <Button size="sm" onClick={() => startSession.mutate()} disabled={startSession.isPending} data-testid={`button-start-${session.id}`}>
              <Play className="mr-1 h-3.5 w-3.5" />
              {startSession.isPending ? "Starting..." : "Go Live"}
            </Button>
          )}
          {isLive && (
            <>
              <Link href={`/live/${session.id}`}>
                <Button size="sm" data-testid={`button-enter-room-${session.id}`}>
                  <Video className="mr-1 h-3.5 w-3.5" />
                  Enter Room
                </Button>
              </Link>
              <Button size="sm" variant="destructive" onClick={() => endSession.mutate()} disabled={endSession.isPending} data-testid={`button-end-${session.id}`}>
                <StopCircle className="mr-1 h-3.5 w-3.5" />
                End
              </Button>
            </>
          )}
          {isEnded && (
            <Link href={`/live/${session.id}`}>
              <Button size="sm" variant="outline" data-testid={`button-review-${session.id}`}>
                <Eye className="mr-1 h-3.5 w-3.5" />
                Review
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function ProviderClassCard({ cls }: { cls: Class & { sessions: Session[]; providerName?: string; shareUrl?: string | null } }) {
  const lang = LANGUAGES.find((l) => l.value === cls.language);
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const [expanded, setExpanded] = useState(true);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const deleteClass = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/classes/${cls.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      toast({ title: "Class deleted" });
    },
  });

  const liveSessions = (cls.sessions?.filter((s) => s.status === "LIVE") || []).sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  const upcomingSessions = (cls.sessions?.filter((s) => s.status === "SCHEDULED") || []).sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  const pastSessions = (cls.sessions?.filter((s) => s.status === "ENDED") || []).sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime());
  const totalSessions = cls.sessions?.length || 0;

  return (
    <div className="glass-panel depth-shadow-sm overflow-visible" data-testid={`provider-class-${cls.id}`}>
      <div className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <img src={cls.thumbnailUrl || DEFAULT_THUMBNAIL} alt="" className="h-20 w-32 rounded-md object-cover hidden sm:block shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold text-lg truncate">{cls.title}</h3>
              {isAdmin && cls.providerName && (
                <p className="mt-0.5 text-xs text-primary flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {cls.providerName}
                </p>
              )}
              {cls.description && (
                <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{cls.description}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {lang && <Badge variant="outline">{getFlagEmoji(lang.flag)} {lang.label}</Badge>}
                {cls.category && <Badge variant="secondary">{cls.category}</Badge>}
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {totalSessions} sessions
                </span>
                {liveSessions.length > 0 && (
                  <Badge variant="default" className="bg-red-500/80 border-red-500/40 text-[10px]">
                    <LiveBroadcastIcon size={12} className="mr-1" /> {liveSessions.length} Live
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <ScheduleSessionDialog classId={cls.id} />
            <EditClassDialog cls={cls} />
            <Button
              size="icon"
              variant="ghost"
              onClick={() => {
                if (confirm("Delete this class and all its sessions?")) deleteClass.mutate();
              }}
              data-testid={`button-delete-class-${cls.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setExpanded(!expanded)}
              data-testid={`button-expand-class-${cls.id}`}
            >
              <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
            </Button>
          </div>
        </div>

        {cls.shareUrl && (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-muted/20 border border-white/5 px-3 py-2">
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="text-xs font-medium shrink-0">Share:</span>
            <code className="text-[11px] text-muted-foreground truncate flex-1" data-testid={`text-class-share-url-${cls.id}`}>
              {cls.shareUrl}
            </code>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 shrink-0 gap-1"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(cls.shareUrl!);
                  setCopiedLink(true);
                  toast({ title: "Share link copied!" });
                  setTimeout(() => setCopiedLink(false), 2000);
                } catch {}
              }}
              data-testid={`button-copy-class-share-${cls.id}`}
            >
              {copiedLink ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
              <span className="text-xs">{copiedLink ? "Copied" : "Copy"}</span>
            </Button>
          </div>
        )}
      </div>

      {expanded && cls.sessions && cls.sessions.length > 0 && (() => {
        const SESSION_LIMIT = 7;
        const allOrdered = [...upcomingSessions, ...liveSessions, ...pastSessions];
        const visibleSessions = showAllSessions ? allOrdered : allOrdered.slice(0, SESSION_LIMIT);
        const hiddenCount = allOrdered.length - SESSION_LIMIT;
        return (
          <div className="border-t px-5 py-3 space-y-2">
            {visibleSessions.map((s) => (
              <SessionRow key={s.id} session={s} classTitle={cls.title} />
            ))}
            {hiddenCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAllSessions(!showAllSessions)}
                className="w-full text-xs text-muted-foreground hover:text-foreground gap-1.5"
                data-testid={`button-toggle-sessions-${cls.id}`}
              >
                {showAllSessions ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" />
                    Show {hiddenCount} more session{hiddenCount !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            )}
          </div>
        );
      })()}

      {expanded && (!cls.sessions || cls.sessions.length === 0) && (
        <div className="border-t border-white/5 px-5 py-6 text-center">
          <Calendar className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">No sessions yet. Schedule one to get started.</p>
        </div>
      )}
    </div>
  );
}

function SessionRow({ session, classTitle }: { session: Session; classTitle: string }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const startSession = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/sessions/${session.id}/start`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      toast({ title: "Session is live!" });
      navigate(`/live/${session.id}`);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const endSession = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/sessions/${session.id}/end`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      toast({ title: "Session ended" });
    },
  });

  const deleteSession = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/sessions/${session.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      toast({ title: "Session deleted" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const isLive = session.status === "LIVE";
  const isScheduled = session.status === "SCHEDULED";
  const isEnded = session.status === "ENDED";

  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-md p-3 ${isLive ? "bg-red-500/5 border border-red-500/20" : "bg-muted/40"}`} data-testid={`session-row-${session.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        {isLive && (
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
          </span>
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {format(new Date(session.startsAt), "MMM d, yyyy 'at' h:mm a")}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            <Badge variant={isLive ? "default" : isEnded ? "secondary" : "outline"} className={`text-[10px] ${isLive ? "bg-red-500/80 border-red-500/40" : ""}`}>
              {session.status}
            </Badge>
            <span className="text-[10px] text-muted-foreground">{session.visibility}</span>
            <span className="text-[10px] text-muted-foreground">{session.chatMode}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <SessionSettingsDialog session={session} />
        {isScheduled && <EditSessionTimeDialog session={session} />}
        {isScheduled && (
          <Button
            size="sm"
            className="btn-gradient-capsule text-xs btn-cta-glow"
            onClick={() => startSession.mutate()}
            disabled={startSession.isPending}
            data-testid={`button-start-${session.id}`}
          >
            <Play className="mr-1 h-3 w-3" />
            Go Live
          </Button>
        )}
        {isLive && (
          <>
            <Link href={`/live/${session.id}`}>
              <Button size="sm" data-testid={`button-enter-room-${session.id}`}>
                <Video className="mr-1 h-3 w-3" />
                Enter Room
              </Button>
            </Link>
            <Button size="sm" variant="destructive" onClick={() => endSession.mutate()} disabled={endSession.isPending} data-testid={`button-end-${session.id}`}>
              <StopCircle className="mr-1 h-3 w-3" />
              End
            </Button>
          </>
        )}
        {isEnded && (
          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => deleteSession.mutate()} disabled={deleteSession.isPending} data-testid={`button-delete-session-${session.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function GoLiveNowDialog() {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [selectedClassId, setSelectedClassId] = useState("");

  const { data: classes } = useQuery<(Class & { sessions: Session[] })[]>({
    queryKey: ["/api/provider/classes"],
  });

  const goLive = useMutation({
    mutationFn: async () => {
      const sessionRes = await apiRequest("POST", `/api/classes/${selectedClassId}/sessions`, {
        startsAt: new Date().toISOString(),
        classId: selectedClassId,
        visibility: "PUBLIC",
        chatMode: "OPEN",
        allowReactions: true,
        allowViewerMic: false,
        allowViewerCam: false,
      });
      const sessionData = await sessionRes.json();
      await apiRequest("POST", `/api/sessions/${sessionData.id}/start`);
      return sessionData.id;
    },
    onSuccess: (sessionId: string) => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      setOpen(false);
      toast({ title: "You're live!" });
      navigate(`/live/${sessionId}`);
    },
    onError: (err: Error) => {
      toast({ title: "Error going live", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive" className="btn-gradient-red" data-testid="button-go-live-now">
          <Zap className="mr-1.5 h-4 w-4" />
          Go Live Now
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-dialog">
        <DialogHeader>
          <DialogTitle>Go Live Now</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mb-4">
          Instantly create a session and start streaming. Pick which class to go live with:
        </p>
        {(!classes || classes.length === 0) ? (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">No classes found. Create a class first.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <Select value={selectedClassId} onValueChange={setSelectedClassId}>
              <SelectTrigger data-testid="select-go-live-class">
                <SelectValue placeholder="Select a class" />
              </SelectTrigger>
              <SelectContent>
                {classes.map((cls) => (
                  <SelectItem key={cls.id} value={cls.id}>{cls.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => goLive.mutate()}
              variant="destructive"
              className="w-full btn-gradient-red"
              disabled={!selectedClassId || goLive.isPending}
              data-testid="button-confirm-go-live"
            >
              {goLive.isPending ? (
                <>Starting stream...</>
              ) : (
                <>
                  <Zap className="mr-1.5 h-4 w-4" />
                  Start Streaming
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateClassDialog() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [language, setLanguage] = useState("ENGLISH");
  const [category, setCategory] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";

  const { data: providersList } = useQuery<{ id: string; name: string; email: string; role: string }[]>({
    queryKey: ["/api/provider/providers-list"],
    enabled: isAdmin && open,
  });

  const create = useMutation({
    mutationFn: async () => {
      const body: any = {
        title, description, language, category: category || undefined,
      };
      if (isAdmin && selectedProviderId) {
        body.providerId = selectedProviderId;
      }
      await apiRequest("POST", "/api/classes", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      setOpen(false);
      setTitle(""); setDescription(""); setCategory(""); setSelectedProviderId("");
      toast({ title: "Class created" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="button-create-class">
          <Plus className="mr-1 h-4 w-4" />
          New Class
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-dialog max-h-[85vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Create New Class</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="space-y-4">
          {isAdmin && providersList && providersList.length > 0 && (
            <div className="space-y-2">
              <Label>Assign to Provider</Label>
              <Select value={selectedProviderId || "self"} onValueChange={(v) => setSelectedProviderId(v === "self" ? "" : v)}>
                <SelectTrigger data-testid="select-provider">
                  <SelectValue placeholder="Select provider..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Myself ({user?.name})</SelectItem>
                  {providersList.filter((p) => p.id !== user?.id).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Choose which provider this class belongs to</p>
            </div>
          )}
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="input-class-title" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="input-class-desc" />
          </div>
          <div className="space-y-2">
            <Label>Language</Label>
            <Select value={language} onValueChange={(v: string) => setLanguage(v)}>
              <SelectTrigger data-testid="select-language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {getFlagEmoji(l.flag)} {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Cooking, Music" data-testid="input-class-category" />
          </div>
          <Button type="submit" className="w-full" disabled={create.isPending} data-testid="button-submit-class">
            {create.isPending ? "Creating..." : "Create Class"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditClassDialog({ cls }: { cls: Class }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(cls.title);
  const [description, setDescription] = useState(cls.description || "");
  const [language, setLanguage] = useState(cls.language);
  const [category, setCategory] = useState(cls.category || "");
  const [thumbnailUrl, setThumbnailUrl] = useState(cls.thumbnailUrl || "");
  const [isUploadingThumb, setIsUploadingThumb] = useState(false);
  const [restrictedWords, setRestrictedWords] = useState<string[]>(cls.restrictedWords || []);
  const [newWord, setNewWord] = useState("");
  const { toast } = useToast();

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }
    setIsUploadingThumb(true);
    try {
      const metaRes = await fetch("/api/uploads/request-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
      });
      if (!metaRes.ok) throw new Error("Failed to get upload URL");
      const { uploadURL, objectPath } = await metaRes.json();
      const uploadRes = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!uploadRes.ok) throw new Error("Failed to upload file");
      setThumbnailUrl(objectPath);
      toast({ title: "Thumbnail uploaded" });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setIsUploadingThumb(false);
    }
  };

  const update = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/classes/${cls.id}`, {
        title, description, language, category: category || null,
        thumbnailUrl: thumbnailUrl || null,
        restrictedWords,
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/sessions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === "string" && key.startsWith("/api/recordings");
      }});
      setOpen(false);
      if (data?.approval) {
        queryClient.invalidateQueries({ queryKey: ["/api/provider/approvals"] });
        toast({ title: "Change submitted for approval", description: data.message });
      } else {
        toast({ title: "Class updated" });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" data-testid={`button-edit-class-${cls.id}`}>
          <Edit className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-dialog">
        <DialogHeader>
          <DialogTitle>Edit Class</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); update.mutate(); }} className="space-y-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="input-edit-class-title" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="input-edit-class-desc" />
          </div>
          <div className="space-y-2">
            <Label>Thumbnail</Label>
            <div className="flex gap-2">
              <Input value={thumbnailUrl} onChange={(e) => setThumbnailUrl(e.target.value)} placeholder="Paste URL or upload below" className="flex-1" data-testid="input-edit-class-thumbnail" />
              {thumbnailUrl && (
                <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => setThumbnailUrl("")} data-testid="button-clear-thumbnail">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor={`thumb-upload-${cls.id}`} className="cursor-pointer inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                <Plus className="h-3 w-3" />
                {isUploadingThumb ? "Uploading..." : "Upload image"}
              </Label>
              <input id={`thumb-upload-${cls.id}`} type="file" accept="image/*" className="hidden" onChange={handleThumbnailUpload} disabled={isUploadingThumb} data-testid="input-upload-class-thumbnail" />
            </div>
            {thumbnailUrl && (
              <div className="mt-1 rounded-md overflow-hidden border border-border/40 w-full max-w-[200px]">
                <img src={thumbnailUrl} alt="Thumbnail preview" className="w-full aspect-video object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} data-testid="img-thumbnail-preview" />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Language</Label>
            <Select value={language} onValueChange={(v) => setLanguage(v as typeof language)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {getFlagEmoji(l.flag)} {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Cooking, Music" data-testid="input-edit-class-category" />
          </div>
          <div className="space-y-2">
            <Label>Restricted Words</Label>
            <p className="text-xs text-muted-foreground">Messages containing these words will be blocked in chat.</p>
            <div className="flex gap-2">
              <Input
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                placeholder="Add a word..."
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const w = newWord.trim().toLowerCase();
                    if (w && !restrictedWords.includes(w)) {
                      setRestrictedWords([...restrictedWords, w]);
                      setNewWord("");
                    }
                  }
                }}
                data-testid="input-restricted-word"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const w = newWord.trim().toLowerCase();
                  if (w && !restrictedWords.includes(w)) {
                    setRestrictedWords([...restrictedWords, w]);
                    setNewWord("");
                  }
                }}
                data-testid="button-add-restricted-word"
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            {restrictedWords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="list-restricted-words">
                {restrictedWords.map((word) => (
                  <Badge key={word} variant="secondary" className="text-xs gap-1 pr-1">
                    {word}
                    <button
                      type="button"
                      onClick={() => setRestrictedWords(restrictedWords.filter((w) => w !== word))}
                      className="ml-0.5 hover:text-destructive"
                      data-testid={`button-remove-word-${word}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={update.isPending || isUploadingThumb} data-testid="button-submit-edit-class">
            {update.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditSessionTimeDialog({ session }: { session: Session }) {
  const [open, setOpen] = useState(false);
  const startsAtDate = new Date(session.startsAt);
  const [date, setDate] = useState(format(startsAtDate, "yyyy-MM-dd"));
  const [time, setTime] = useState(format(startsAtDate, "HH:mm"));
  const { toast } = useToast();

  const reschedule = useMutation({
    mutationFn: async () => {
      const newStartsAt = new Date(`${date}T${time}:00`);
      if (isNaN(newStartsAt.getTime())) throw new Error("Invalid date/time");
      if (newStartsAt <= new Date()) throw new Error("Cannot schedule in the past");
      const res = await apiRequest("PATCH", `/api/sessions/${session.id}/schedule`, {
        startsAt: newStartsAt.toISOString(),
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/sessions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      setOpen(false);
      if (data?.approval) {
        queryClient.invalidateQueries({ queryKey: ["/api/provider/approvals"] });
        toast({ title: "Reschedule submitted for approval", description: data.message });
      } else {
        toast({ title: "Session rescheduled" });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => {
      setOpen(v);
      if (v) {
        const d = new Date(session.startsAt);
        setDate(format(d, "yyyy-MM-dd"));
        setTime(format(d, "HH:mm"));
      }
    }}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" data-testid={`button-edit-session-time-${session.id}`}>
          <Clock className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-dialog">
        <DialogHeader>
          <DialogTitle>Reschedule Session</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); reschedule.mutate(); }} className="space-y-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <DatePicker value={date} onChange={setDate} disablePast data-testid="input-edit-session-date" />
          </div>
          <div className="space-y-2">
            <Label>Time</Label>
            <TimePicker value={time} onChange={setTime} data-testid="input-edit-session-time" />
          </div>
          <p className="text-xs text-muted-foreground">
            New scheduled time: {(() => { try { if (!date || !time) return "—"; const d = new Date(`${date}T${time}:00`); return isNaN(d.getTime()) ? "—" : format(d, "MMM d, yyyy 'at' h:mm a"); } catch { return "—"; } })()}
          </p>
          <Button type="submit" className="w-full" disabled={reschedule.isPending} data-testid="button-submit-reschedule">
            {reschedule.isPending ? "Saving..." : "Reschedule"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface CohostInfo {
  id: string;
  userId: string;
  userName: string;
  avatarUrl: string | null;
  status: string;
}

function SessionCohostManager({ sessionId }: { sessionId: string }) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");

  const { data: cohosts = [], isLoading: cohostsLoading } = useQuery<CohostInfo[]>({
    queryKey: ["/api/sessions", sessionId, "cohosts"],
  });

  const { data: searchResults } = useQuery<{ id: string; name: string; email: string; displayName: string | null }[]>({
    queryKey: [`/api/providers/search?q=${encodeURIComponent(searchQuery)}`],
    enabled: searchQuery.length >= 2,
  });

  const addCohost = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("POST", `/api/sessions/${sessionId}/cohosts`, { userId });
    },
    onSuccess: () => {
      toast({ title: "Co-host invited" });
      setSearchQuery("");
      queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "cohosts"] });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const removeCohost = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("DELETE", `/api/sessions/${sessionId}/cohosts/${userId}`);
    },
    onSuccess: () => {
      toast({ title: "Co-host removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "cohosts"] });
    },
  });

  const existingUserIds = new Set(cohosts.map((c) => c.userId));
  const filteredResults = (searchResults || []).filter((r) => !existingUserIds.has(r.id));

  return (
    <div className="space-y-4" data-testid="session-cohost-manager">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search educators to invite..."
          className="pl-9"
          data-testid="input-search-cohost-dashboard"
        />
        {searchQuery && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSearchQuery("")}
            className="absolute right-1 top-1"
            data-testid="button-clear-cohost-search"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {filteredResults.length > 0 && (
        <div className="max-h-40 overflow-y-auto rounded-md border">
          {filteredResults.map((provider) => (
            <Button
              key={provider.id}
              variant="ghost"
              onClick={() => addCohost.mutate(provider.id)}
              disabled={addCohost.isPending}
              className="flex w-full items-center gap-3 px-3 py-2 justify-start rounded-none border-b last:border-b-0 h-auto"
              data-testid={`button-invite-cohost-${provider.id}`}
            >
              <Avatar className="h-7 w-7">
                <AvatarFallback className="text-xs">
                  {(provider.displayName || provider.name)?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-sm font-medium truncate">{provider.displayName || provider.name}</p>
                <p className="text-xs text-muted-foreground truncate">{provider.email}</p>
              </div>
              <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
            </Button>
          ))}
        </div>
      )}

      {searchQuery.length >= 2 && filteredResults.length === 0 && !cohostsLoading && (
        <p className="text-sm text-muted-foreground text-center py-2" data-testid="text-no-educators-found">No matching educators found</p>
      )}

      <div>
        <Label className="flex items-center gap-2 mb-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          Invited Co-Hosts ({cohosts.length})
        </Label>
        {cohosts.length > 0 ? (
          <div className="space-y-1.5">
            {cohosts.map((cohost) => (
              <div key={cohost.userId} className="flex items-center gap-3 rounded-md border px-3 py-2" data-testid={`cohost-item-${cohost.userId}`}>
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="text-xs">
                    {cohost.userName?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" data-testid={`text-cohost-name-${cohost.userId}`}>{cohost.userName}</p>
                  <Badge variant={cohost.status === "ACCEPTED" ? "default" : "outline"} className="text-[10px] px-1.5 py-0" data-testid={`badge-cohost-status-${cohost.userId}`}>
                    {cohost.status === "ACCEPTED" ? "Accepted" : "Invited"}
                  </Badge>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeCohost.mutate(cohost.userId)}
                  disabled={removeCohost.isPending}
                  data-testid={`button-remove-cohost-${cohost.userId}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4 border rounded-md" data-testid="text-no-cohosts">
            No co-hosts yet. Search above to invite educators.
          </p>
        )}
      </div>
    </div>
  );
}

function SessionSettingsDialog({ session, defaultTab = "settings", children }: { session: Session; defaultTab?: string; children?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [chatMode, setChatMode] = useState(session.chatMode);
  const [allowReactions, setAllowReactions] = useState(session.allowReactions);
  const [allowViewerMic, setAllowViewerMic] = useState(session.allowViewerMic);
  const [allowViewerCam, setAllowViewerCam] = useState(session.allowViewerCam);
  const [visibility, setVisibility] = useState(session.visibility);
  const { toast } = useToast();

  const updateSettings = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/sessions/${session.id}/settings`, {
        chatMode, allowReactions, allowViewerMic, allowViewerCam, visibility,
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      setOpen(false);
      if (data?.approval) {
        queryClient.invalidateQueries({ queryKey: ["/api/provider/approvals"] });
        toast({ title: "Change submitted for approval", description: data.message });
      } else {
        toast({ title: "Session settings updated" });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button size="icon" variant="ghost" data-testid={`button-settings-${session.id}`}>
            <Settings className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg glass-dialog">
        <DialogHeader>
          <DialogTitle>Session Settings</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="settings" className="flex-1" data-testid="tab-settings">
              <Settings className="h-3.5 w-3.5 mr-1.5" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="cohosts" className="flex-1" data-testid="tab-cohosts">
              <Users className="h-3.5 w-3.5 mr-1.5" />
              Co-Hosts
            </TabsTrigger>
          </TabsList>
          <TabsContent value="settings" className="mt-4">
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Visibility</Label>
                <Select value={visibility} onValueChange={(v: string) => setVisibility(v as typeof visibility)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUBLIC">Public</SelectItem>
                    <SelectItem value="UNLISTED">Unlisted</SelectItem>
                    <SelectItem value="PRIVATE">Private</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Chat Mode</Label>
                <Select value={chatMode} onValueChange={(v: string) => setChatMode(v as typeof chatMode)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">Open</SelectItem>
                    <SelectItem value="MOD_ONLY">Moderators Only</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  Allow Reactions
                </Label>
                <Switch checked={allowReactions} onCheckedChange={setAllowReactions} data-testid="switch-reactions" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  Allow Viewer Mic
                </Label>
                <Switch checked={allowViewerMic} onCheckedChange={setAllowViewerMic} data-testid="switch-viewer-mic" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Video className="h-4 w-4 text-muted-foreground" />
                  Allow Viewer Cam
                </Label>
                <Switch checked={allowViewerCam} onCheckedChange={setAllowViewerCam} data-testid="switch-viewer-cam" />
              </div>
              <Button onClick={() => updateSettings.mutate()} className="w-full" disabled={updateSettings.isPending} data-testid="button-save-settings">
                {updateSettings.isPending ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="cohosts" className="mt-4">
            <SessionCohostManager sessionId={session.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

const DAYS_OF_WEEK = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
] as const;

function generateRecurringDates(
  startDate: Date,
  startTime: string,
  frequency: string,
  interval: number,
  selectedDays: number[],
  endType: string,
  endDate: string,
  endAfterCount: number,
): Date[] {
  const dates: Date[] = [];
  const maxDates = 52;
  const [hours, minutes] = startTime.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return [];

  if (frequency === "none") {
    const d = new Date(startDate);
    d.setHours(hours, minutes, 0, 0);
    return [d];
  }

  let currentDate = startOfDay(startDate);
  const endLimit = endType === "on_date" && endDate
    ? (() => { const ed = new Date(endDate + "T23:59:59"); return isNaN(ed.getTime()) ? null : ed; })()
    : null;
  const countLimit = endType === "after" ? endAfterCount : maxDates;
  const neverLimit = endType === "never" ? maxDates : countLimit;
  const limit = Math.min(neverLimit, maxDates);

  if (frequency === "daily") {
    while (dates.length < limit) {
      const d = new Date(currentDate);
      d.setHours(hours, minutes, 0, 0);
      if (endLimit && d > endLimit) break;
      dates.push(d);
      currentDate = addDays(currentDate, interval);
    }
  } else if (frequency === "weekly") {
    const safeDays = selectedDays.length > 0 ? [...selectedDays].sort((a, b) => a - b) : [getDay(startDate)];
    let weekStart = startOfDay(startDate);
    let weeksAdded = 0;
    const originStart = startOfDay(startDate);
    while (dates.length < limit) {
      for (const dayNum of safeDays) {
        const candidateDate = setDay(weekStart, dayNum, { weekStartsOn: 0 });
        if (startOfDay(candidateDate) < originStart) continue;
        const d = new Date(candidateDate);
        d.setHours(hours, minutes, 0, 0);
        if (endLimit && d > endLimit) return dates;
        if (dates.length >= limit) return dates;
        dates.push(d);
      }
      weeksAdded++;
      weekStart = addWeeks(originStart, weeksAdded * interval);
    }
  } else if (frequency === "monthly") {
    while (dates.length < limit) {
      const d = new Date(currentDate);
      d.setHours(hours, minutes, 0, 0);
      if (endLimit && d > endLimit) break;
      dates.push(d);
      currentDate = addMonths(currentDate, interval);
    }
  }

  return dates;
}

function ScheduleSessionDialog({ classId }: { classId: string }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("13:00");
  const [visibility, setVisibility] = useState("PUBLIC");
  const [chatMode, setChatMode] = useState("OPEN");

  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [interval, setInterval] = useState(1);
  const [selectedDays, setSelectedDays] = useState<number[]>([0]);
  const [endType, setEndType] = useState<"on_date" | "after" | "never">("on_date");
  const [endDate, setEndDate] = useState("");
  const [endAfterCount, setEndAfterCount] = useState(10);

  const previewDates = (() => {
    if (!startDate) return [];
    const sd = new Date(startDate + "T00:00:00");
    if (isNaN(sd.getTime())) return [];
    return generateRecurringDates(
      sd,
      startTime,
      repeatEnabled ? frequency : "none",
      interval,
      selectedDays,
      endType,
      endDate,
      endAfterCount,
    );
  })();

  const schedule = useMutation({
    mutationFn: async () => {
      if (previewDates.length === 0) throw new Error("No dates to schedule");
      if (previewDates.length === 1) {
        await apiRequest("POST", `/api/classes/${classId}/sessions`, {
          startsAt: previewDates[0].toISOString(),
          classId,
          visibility,
          chatMode,
          allowReactions: true,
          allowViewerMic: false,
          allowViewerCam: false,
        });
      } else {
        await apiRequest("POST", `/api/classes/${classId}/sessions/batch`, {
          dates: previewDates.map((d) => d.toISOString()),
          visibility,
          chatMode,
          allowReactions: true,
          allowViewerMic: false,
          allowViewerCam: false,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/provider/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/provider/stats"] });
      setOpen(false);
      resetForm();
      toast({ title: `${previewDates.length} session${previewDates.length > 1 ? "s" : ""} scheduled` });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  function resetForm() {
    setStartDate("");
    setStartTime("13:00");
    setRepeatEnabled(false);
    setFrequency("weekly");
    setInterval(1);
    setSelectedDays([0]);
    setEndType("on_date");
    setEndDate("");
    setEndAfterCount(10);
  }

  function toggleDay(day: number) {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`button-schedule-${classId}`}>
          <Calendar className="mr-1 h-3 w-3" />
          Schedule
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto glass-dialog">
        <DialogHeader>
          <DialogTitle>Schedule Session</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="font-semibold text-sm">Event Time</Label>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex-1 min-w-[140px]">
                <DatePicker value={startDate} onChange={setStartDate} disablePast data-testid="input-start-date" />
              </div>
              <div className="w-[160px]">
                <TimePicker value={startTime} onChange={setStartTime} data-testid="input-start-time" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Sessions end when you stop the stream</p>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              checked={repeatEnabled}
              onCheckedChange={setRepeatEnabled}
              data-testid="switch-repeat"
            />
            <Label className="font-semibold text-sm cursor-pointer" onClick={() => setRepeatEnabled(!repeatEnabled)}>
              Repeat Event
            </Label>
          </div>

          {repeatEnabled && (
            <div className="glass-panel p-4 space-y-4">
              <div className="space-y-2">
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="select-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-semibold text-sm">Occurs Every</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    value={interval}
                    onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20"
                    data-testid="input-interval"
                  />
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger className="flex-1" data-testid="select-interval-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Day{interval > 1 ? "s" : ""}</SelectItem>
                      <SelectItem value="weekly">Week{interval > 1 ? "s" : ""}</SelectItem>
                      <SelectItem value="monthly">Month{interval > 1 ? "s" : ""}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {frequency === "weekly" && (
                <div className="space-y-2">
                  <Label className="font-semibold text-sm">Repeats On</Label>
                  <div className="flex flex-wrap gap-1.5" data-testid="day-selector">
                    {DAYS_OF_WEEK.map((day) => (
                      <Button
                        key={day.value}
                        type="button"
                        size="sm"
                        variant={selectedDays.includes(day.value) ? "default" : "outline"}
                        onClick={() => toggleDay(day.value)}
                        className="px-3"
                        data-testid={`button-day-${day.label.toLowerCase()}`}
                      >
                        {day.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Label className="font-semibold text-sm">Repeat Ends</Label>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="endType"
                      checked={endType === "on_date"}
                      onChange={() => setEndType("on_date")}
                      className="accent-primary"
                      data-testid="radio-end-on-date"
                    />
                    On Date
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="endType"
                      checked={endType === "after"}
                      onChange={() => setEndType("after")}
                      className="accent-primary"
                      data-testid="radio-end-after"
                    />
                    After
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="endType"
                      checked={endType === "never"}
                      onChange={() => setEndType("never")}
                      className="accent-primary"
                      data-testid="radio-end-never"
                    />
                    Never
                  </label>
                </div>

                {endType === "on_date" && (
                  <DatePicker value={endDate} onChange={setEndDate} disablePast data-testid="input-end-date" />
                )}
                {endType === "after" && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={52}
                      value={endAfterCount}
                      onChange={(e) => setEndAfterCount(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20"
                      data-testid="input-end-after-count"
                    />
                    <span className="text-sm text-muted-foreground">occurrences</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {previewDates.length > 0 && (
            <div className="space-y-2">
              <Label className="font-semibold text-sm">
                Preview ({previewDates.length} session{previewDates.length > 1 ? "s" : ""})
              </Label>
              <div className="max-h-32 overflow-y-auto rounded-md bg-muted/40 p-2 space-y-1" data-testid="schedule-preview">
                {previewDates.slice(0, 20).map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <Calendar className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span>{format(d, "EEE, MMM d, yyyy 'at' h:mm a")}</span>
                  </div>
                ))}
                {previewDates.length > 20 && (
                  <p className="text-xs text-muted-foreground text-center">
                    + {previewDates.length - 20} more sessions
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">Visibility</Label>
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger data-testid="select-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PUBLIC">Public</SelectItem>
                  <SelectItem value="UNLISTED">Unlisted</SelectItem>
                  <SelectItem value="PRIVATE">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Chat Mode</Label>
              <Select value={chatMode} onValueChange={setChatMode}>
                <SelectTrigger data-testid="select-chat-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="MOD_ONLY">Moderators Only</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            onClick={() => schedule.mutate()}
            className="w-full"
            disabled={!startDate || schedule.isPending || previewDates.length === 0}
            data-testid="button-submit-session"
          >
            {schedule.isPending
              ? "Scheduling..."
              : previewDates.length > 1
                ? `Schedule ${previewDates.length} Sessions`
                : "Schedule Session"
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type ProviderRecording = Recording & { classTitle: string; sessionStartsAt: Date };

const TAG_COLORS = [
  "#06b6d4", "#8b5cf6", "#f59e0b", "#ef4444", "#10b981",
  "#3b82f6", "#ec4899", "#f97316", "#6366f1", "#14b8a6",
];

function ProviderRecordingsTab() {
  const { toast } = useToast();
  const [editingRecording, setEditingRecording] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editPublic, setEditPublic] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState<1 | 2>(1);
  const [trimRecording, setTrimRecording] = useState<ProviderRecording | null>(null);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);

  const { data: myRecordings, isLoading: loadingRecs } = useQuery<ProviderRecording[]>({
    queryKey: ["/api/recordings/my"],
  });

  const { data: myTags, isLoading: loadingTags } = useQuery<RecordingTag[]>({
    queryKey: ["/api/recordings/tags/my"],
  });

  const createTag = useMutation({
    mutationFn: () => apiRequest("POST", "/api/recordings/tags", { name: newTagName, color: newTagColor }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/tags/my"] });
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/tags"] });
      setNewTagName("");
      toast({ title: "Tag created" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteTag = useMutation({
    mutationFn: (tagId: string) => apiRequest("DELETE", `/api/recordings/tags/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/tags/my"] });
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/tags"] });
      toast({ title: "Tag deleted" });
    },
  });

  const updateRecording = useMutation({
    mutationFn: (data: { id: string; title: string; description: string; tags: string[]; isPublic: boolean }) =>
      apiRequest("PATCH", `/api/recordings/${data.id}`, {
        title: data.title,
        description: data.description,
        tags: data.tags,
        isPublic: data.isPublic,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/my"] });
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/library"] });
      setEditingRecording(null);
      toast({ title: "Recording updated" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteRecording = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/recordings/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/my"] });
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/library"] });
      setDeleteConfirmId(null);
      setDeleteConfirmStep(1);
      toast({ title: "Recording deleted permanently" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const trimMutation = useMutation({
    mutationFn: async ({ id, trimStart, trimEnd }: { id: string; trimStart: number; trimEnd: number }) => {
      const res = await apiRequest("POST", `/api/recordings/${id}/trim`, { trimStart, trimEnd });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/my"] });
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/library"] });
      setTrimRecording(null);
      toast({ title: "Recording trimmed successfully" });
    },
    onError: (err: any) => toast({ title: "Trim failed", description: err.message, variant: "destructive" }),
  });

  function openTrim(rec: ProviderRecording) {
    setTrimRecording(rec);
    setTrimStart(0);
    setTrimEnd(rec.durationSeconds || 0);
  }

  function startEditing(rec: ProviderRecording) {
    setEditingRecording(rec.id);
    setEditTitle(rec.title || rec.classTitle || "");
    setEditDescription(rec.description || "");
    setEditTags(rec.tags || []);
    setEditPublic(rec.isPublic);
  }

  function toggleEditTag(tagName: string) {
    setEditTags((prev) =>
      prev.includes(tagName) ? prev.filter((t) => t !== tagName) : [...prev, tagName]
    );
  }

  function fmtDuration(seconds: number | null): string {
    if (!seconds) return "--:--";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    return `${m}:${s.toString().padStart(2, "0")}`;
  }

  const retryRecording = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/recordings/${id}/retry`);
      return await res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/recordings/my"] });
      if (data.assembled) {
        toast({ title: "Recording assembled from saved chunks" });
      } else {
        toast({ title: "Recording set for retry" });
      }
    },
    onError: (err: any) => toast({ title: "Retry failed", description: err.message, variant: "destructive" }),
  });

  const readyRecordings = (myRecordings || []).filter((r) => r.status === "READY");
  const pendingRecordings = (myRecordings || []).filter((r) => r.status !== "READY" && r.status !== "FAILED" && r.status !== "PARTIAL");
  const failedRecordings = (myRecordings || []).filter((r) => r.status === "FAILED");
  const partialRecordings = (myRecordings || []).filter((r) => r.status === "PARTIAL");

  return (
    <div className="space-y-6">
      <div className="glass-panel p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Tag className="h-4 w-4 text-primary" />
            My Recording Tags
          </h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {(myTags || []).map((tag) => (
            <Badge
              key={tag.id}
              style={{ backgroundColor: tag.color }}
              className="flex items-center gap-1 text-white"
              data-testid={`badge-tag-${tag.name}`}
            >
              {tag.name}
              <button
                onClick={() => deleteTag.mutate(tag.id)}
                className="ml-0.5 rounded-full p-0.5 opacity-80 hover:opacity-100"
                data-testid={`button-delete-tag-${tag.name}`}
              >
                <Trash2 className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
          {(!myTags || myTags.length === 0) && !loadingTags && (
            <p className="text-xs text-muted-foreground">No tags yet. Create tags to categorize your recordings.</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Tag name (e.g. Basics, Advanced)"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            className="flex-1 min-w-[150px] max-w-[250px]"
            data-testid="input-new-tag-name"
          />
          <div className="flex gap-1">
            {TAG_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setNewTagColor(c)}
                className="h-6 w-6 rounded-full border-2"
                style={{
                  backgroundColor: c,
                  borderColor: newTagColor === c ? "white" : "transparent",
                }}
                data-testid={`button-color-${c}`}
              />
            ))}
          </div>
          <Button
            size="sm"
            disabled={!newTagName.trim() || createTag.isPending}
            onClick={() => createTag.mutate()}
            data-testid="button-create-tag"
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Tag
          </Button>
        </div>
      </div>

      {pendingRecordings.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <CircleDot className="h-3.5 w-3.5 text-amber-400 animate-pulse" />
            Processing ({pendingRecordings.length})
          </h3>
          <div className="space-y-2">
            {pendingRecordings.map((rec) => (
              <div key={rec.id} className="glass-panel-sm p-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">{rec.classTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(rec.sessionStartsAt), "MMM d, yyyy h:mm a")}
                  </p>
                </div>
                <Badge variant="secondary">{rec.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
          <Video className="h-3.5 w-3.5 text-primary" />
          Ready Recordings ({readyRecordings.length})
        </h3>

        {loadingRecs ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-md" />
            ))}
          </div>
        ) : readyRecordings.length === 0 ? (
          <div className="glass-panel p-8 text-center">
            <Video className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No recordings yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Recordings are created automatically when you stream</p>
          </div>
        ) : (
          <div className="space-y-2">
            {readyRecordings.map((rec) => (
              <div key={rec.id} className="glass-panel-sm p-4" data-testid={`card-my-recording-${rec.id}`}>
                {editingRecording === rec.id ? (
                  <div className="space-y-3">
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Recording title"
                      data-testid="input-edit-recording-title"
                    />
                    <Textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Add a description for viewers..."
                      className="resize-none"
                      rows={2}
                      data-testid="input-edit-recording-description"
                    />
                    <div className="space-y-1">
                      <Label className="text-xs">Tags</Label>
                      <div className="flex flex-wrap gap-1">
                        {(myTags || []).map((tag) => (
                          <Badge
                            key={tag.id}
                            variant={editTags.includes(tag.name) ? "default" : "outline"}
                            className="cursor-pointer text-xs"
                            style={editTags.includes(tag.name) ? { backgroundColor: tag.color } : undefined}
                            onClick={() => toggleEditTag(tag.name)}
                            data-testid={`toggle-tag-${tag.name}`}
                          >
                            {editTags.includes(tag.name) && <Check className="mr-0.5 h-2.5 w-2.5" />}
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editPublic}
                        onCheckedChange={setEditPublic}
                        data-testid="switch-recording-public"
                      />
                      <Label className="text-xs">
                        {editPublic ? "Published to Library" : "Private (only you can see)"}
                      </Label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => updateRecording.mutate({
                          id: rec.id,
                          title: editTitle,
                          description: editDescription,
                          tags: editTags,
                          isPublic: editPublic,
                        })}
                        disabled={updateRecording.isPending}
                        data-testid="button-save-recording"
                      >
                        {updateRecording.isPending ? "Saving..." : "Save"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setEditingRecording(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold truncate" data-testid={`text-my-recording-title-${rec.id}`}>
                          {rec.title || rec.classTitle}
                        </p>
                        {rec.isPublic ? (
                          <Badge variant="default" className="text-[10px] shrink-0">
                            <Globe className="mr-0.5 h-2.5 w-2.5" />
                            Public
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] shrink-0">Private</Badge>
                        )}
                      </div>
                      {rec.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{rec.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>{format(new Date(rec.sessionStartsAt), "MMM d, yyyy")}</span>
                        {rec.durationSeconds && (
                          <>
                            <span>-</span>
                            <span className="flex items-center gap-0.5">
                              <Clock className="h-3 w-3" />
                              {fmtDuration(rec.durationSeconds)}
                            </span>
                          </>
                        )}
                      </div>
                      {rec.tags && rec.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {rec.tags.map((t) => {
                            const tagDef = (myTags || []).find((mt) => mt.name === t);
                            return (
                              <Badge
                                key={t}
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0"
                                style={tagDef ? { backgroundColor: tagDef.color, color: "white" } : undefined}
                              >
                                {t}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {rec.status === "READY" && rec.url && rec.durationSeconds && rec.durationSeconds > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openTrim(rec)}
                          data-testid={`button-trim-recording-${rec.id}`}
                        >
                          <Scissors className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startEditing(rec)}
                        data-testid={`button-edit-recording-${rec.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {deleteConfirmId === rec.id ? (
                        <div className="flex items-center gap-1 animate-in fade-in">
                          {deleteConfirmStep === 1 ? (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setDeleteConfirmStep(2)}
                              data-testid={`button-delete-confirm1-${rec.id}`}
                            >
                              Delete?
                            </Button>
                          ) : (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deleteRecording.mutate(rec.id)}
                              disabled={deleteRecording.isPending}
                              data-testid={`button-delete-confirm2-${rec.id}`}
                            >
                              {deleteRecording.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Permanently Delete"}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setDeleteConfirmId(null); setDeleteConfirmStep(1); }}
                            data-testid={`button-delete-cancel-${rec.id}`}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setDeleteConfirmId(rec.id); setDeleteConfirmStep(1); }}
                          data-testid={`button-delete-recording-${rec.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {partialRecordings.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-orange-400" />
            Partial Recordings ({partialRecordings.length})
          </h3>
          <div className="space-y-2">
            {partialRecordings.map((rec) => (
              <div key={rec.id} className="glass-panel-sm p-3 flex flex-wrap items-center justify-between gap-2 border-orange-400/20" data-testid={`card-partial-recording-${rec.id}`}>
                <div>
                  <p className="text-sm font-medium">{rec.title || rec.classTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(rec.sessionStartsAt), "MMM d, yyyy h:mm a")}
                    {rec.chunkCount > 0 && ` · ${rec.chunkCount} chunk${rec.chunkCount !== 1 ? "s" : ""} recovered`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-orange-500/10 text-orange-600 dark:text-orange-400">Partial</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => retryRecording.mutate(rec.id)}
                    disabled={retryRecording.isPending}
                    data-testid={`button-retry-partial-${rec.id}`}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {failedRecordings.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <StopCircle className="h-3.5 w-3.5 text-red-400" />
            Failed ({failedRecordings.length})
          </h3>
          <div className="space-y-2">
            {failedRecordings.map((rec) => (
              <div key={rec.id} className="glass-panel-sm p-3 flex flex-wrap items-center justify-between gap-2 border-red-400/20">
                <div>
                  <p className="text-sm font-medium">{rec.classTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(rec.sessionStartsAt), "MMM d, yyyy h:mm a")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="destructive">Failed</Badge>
                  {rec.chunkCount > 0 && (
                    <span className="text-[10px] text-muted-foreground">{rec.chunkCount} chunk{rec.chunkCount !== 1 ? "s" : ""}</span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => retryRecording.mutate(rec.id)}
                    disabled={retryRecording.isPending}
                    data-testid={`button-retry-recording-${rec.id}`}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                  {deleteConfirmId === rec.id ? (
                    <div className="flex items-center gap-1 animate-in fade-in">
                      {deleteConfirmStep === 1 ? (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeleteConfirmStep(2)}
                          data-testid={`button-delete-confirm1-${rec.id}`}
                        >
                          Delete?
                        </Button>
                      ) : (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteRecording.mutate(rec.id)}
                          disabled={deleteRecording.isPending}
                          data-testid={`button-delete-confirm2-${rec.id}`}
                        >
                          {deleteRecording.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Permanently Delete"}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setDeleteConfirmId(null); setDeleteConfirmStep(1); }}
                        data-testid={`button-delete-cancel-${rec.id}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setDeleteConfirmId(rec.id); setDeleteConfirmStep(1); }}
                      data-testid={`button-delete-failed-${rec.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!trimRecording} onOpenChange={(open) => !open && setTrimRecording(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Trim Recording</DialogTitle>
          </DialogHeader>
          {trimRecording && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {trimRecording.title || trimRecording.classTitle} ({fmtDuration(trimRecording.durationSeconds)})
              </p>

              {trimRecording.url && (
                <video
                  src={trimRecording.url}
                  controls
                  className="w-full rounded-lg bg-black"
                  data-testid="video-trim-preview"
                />
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>Trim range</span>
                  <span className="font-mono text-muted-foreground">
                    {fmtDuration(trimStart)} — {fmtDuration(trimEnd)}
                  </span>
                </div>
                <div className="px-1">
                  <Slider
                    min={0}
                    max={trimRecording.durationSeconds || 0}
                    step={1}
                    value={[trimStart, trimEnd]}
                    onValueChange={([s, e]) => { setTrimStart(s); setTrimEnd(e); }}
                    data-testid="slider-trim-range"
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0:00</span>
                  <span>{fmtDuration(trimRecording.durationSeconds)}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTrimRecording(null)} data-testid="button-cancel-trim">
                  Cancel
                </Button>
                <Button
                  disabled={trimMutation.isPending || trimStart >= trimEnd}
                  onClick={() => trimMutation.mutate({ id: trimRecording.id, trimStart, trimEnd })}
                  data-testid="button-confirm-trim"
                >
                  {trimMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Scissors className="mr-2 h-4 w-4" />}
                  Trim Recording
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

type ApprovalWithMeta = ScheduleApproval & { requesterName: string; reviewerName: string | null; targetLabel: string };

function ProviderApprovalsTab() {
  const { data: approvals, isLoading } = useQuery<ApprovalWithMeta[]>({
    queryKey: ["/api/provider/approvals"],
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="glass-panel-sm p-4"><Skeleton className="h-16 w-full" /></div>
        ))}
      </div>
    );
  }

  if (!approvals || approvals.length === 0) {
    return (
      <div className="glass-panel p-8 text-center">
        <ClipboardCheck className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground" data-testid="text-no-approvals">No change requests yet</p>
        <p className="mt-1 text-xs text-muted-foreground">When you modify a class or session, your changes will appear here for admin review</p>
      </div>
    );
  }

  const changeTypeLabels: Record<string, string> = {
    CLASS_UPDATE: "Class Update",
    SESSION_SETTINGS_UPDATE: "Session Settings",
    SESSION_RESCHEDULE: "Reschedule",
  };

  const statusStyles: Record<string, string> = {
    PENDING: "bg-amber-500/20 text-amber-400 border-amber-500/40",
    APPROVED: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    REJECTED: "bg-red-500/20 text-red-400 border-red-500/40",
  };

  return (
    <div className="space-y-3" data-testid="provider-approvals-list">
      {approvals.map((a) => (
        <div key={a.id} className="glass-panel-sm p-4" data-testid={`approval-card-${a.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Badge variant="outline" className={statusStyles[a.status] || ""} data-testid={`approval-status-${a.id}`}>
                  {a.status}
                </Badge>
                <Badge variant="secondary" className="text-[10px]">
                  {changeTypeLabels[a.changeType] || a.changeType}
                </Badge>
              </div>
              <p className="text-sm font-medium" data-testid={`approval-target-${a.id}`}>
                {a.targetLabel}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Submitted {format(new Date(a.createdAt), "MMM d, yyyy 'at' h:mm a")}
              </p>
              {a.proposedChanges && (
                <div className="mt-2 text-xs text-muted-foreground bg-muted/20 rounded p-2">
                  <span className="font-medium">Proposed changes:</span>{" "}
                  {Object.entries(a.proposedChanges as Record<string, any>).map(([key, val]) => (
                    <span key={key} className="inline-block mr-2">
                      {key}: <span className="text-foreground">{String(val)}</span>
                    </span>
                  ))}
                </div>
              )}
              {a.status === "REJECTED" && a.reviewerNote && (
                <div className="mt-2 text-xs text-red-400 bg-red-500/10 rounded p-2" data-testid={`approval-note-${a.id}`}>
                  <span className="font-medium">Rejection reason:</span> {a.reviewerNote}
                </div>
              )}
              {a.status === "APPROVED" && a.reviewerName && (
                <p className="mt-1 text-xs text-emerald-400">
                  Approved by {a.reviewerName}
                  {a.reviewedAt && ` on ${format(new Date(a.reviewedAt), "MMM d, yyyy")}`}
                </p>
              )}
              {a.status === "REJECTED" && a.reviewerName && (
                <p className="mt-1 text-xs text-red-400">
                  Rejected by {a.reviewerName}
                  {a.reviewedAt && ` on ${format(new Date(a.reviewedAt), "MMM d, yyyy")}`}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
